/**
 * Markdown 渲染器 - 支持 Mermaid 图表、KaTeX 数学公式、代码高亮等复杂内容
 * Mermaid 渲染逻辑已提取至 MermaidBlock.tsx
 */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import MermaidBlock from './MermaidBlock';
import 'katex/dist/katex.min.css';

/** 从 React children 中递归提取纯文本 */
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (React.isValidElement(children)) {
    return extractTextFromChildren((children.props as any)?.children);
  }
  return '';
};

/**
 * 分段渲染组件：将 mermaid 占位符替换为实际的 MermaidBlock
 * mermaid 块独立于 ReactMarkdown 渲染，确保编辑周围文本时 mermaid 视图状态不会丢失
 */
const SplitMermaidContent: React.FC<{
  content: string;
  charts: string[];
  remarkPlugins: any[];
  rehypePlugins: any[];
  components: any;
  mermaidDefaultView: 'code' | 'chart';
}> = React.memo(({ content, charts, remarkPlugins, rehypePlugins, components, mermaidDefaultView }) => {
  const parts = useMemo(() => {
    const result: Array<{ type: 'markdown'; content: string } | { type: 'mermaid'; chart: string; index: number }> = [];
    const regex = /<div class="mermaid-placeholder" data-index="(\d+)"><\/div>/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(content)) !== null) {
      const before = content.substring(lastIndex, match.index);
      if (before.trim()) result.push({ type: 'markdown', content: before });
      const chartIndex = parseInt(match[1], 10);
      if (chartIndex < charts.length) result.push({ type: 'mermaid', chart: charts[chartIndex], index: chartIndex });
      lastIndex = match.index + match[0].length;
    }
    const after = content.substring(lastIndex);
    if (after.trim()) result.push({ type: 'markdown', content: after });
    return result;
  }, [content, charts]);

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'mermaid') {
          return <MermaidBlock key={`mermaid-pos-${part.index}`} chart={part.chart} defaultView={mermaidDefaultView} />;
        }
        return (
          <ReactMarkdown key={`md-segment-${i}`} remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
            {part.content}
          </ReactMarkdown>
        );
      })}
    </>
  );
});
SplitMermaidContent.displayName = 'SplitMermaidContent';

interface MarkdownRendererProps {
  content: string;
  enableMermaid?: boolean;
  enableMath?: boolean;
  className?: string;
  mermaidDefaultView?: 'code' | 'chart';
}

/** 将公式中的裸 CJK 字符包裹在 \text{} 中，避免 KaTeX strict mode 警告 */
const CJK_RANGE = '\\u4e00-\\u9fff\\u3400-\\u4dbf\\uf900-\\ufaff\\u3000-\\u303f\\uff00-\\uffef';
const CJK_RE = new RegExp(`[${CJK_RANGE}]+`, 'g');
const MATH_BLOCK = /(\$\$[\s\S]*?\$\$|\$[^$\n]+?\$)/g;

const wrapCjkInFormula = (formula: string): string => {
  // 1. 保护已有的 \text{...} 块，避免重复包裹
  const texts: string[] = [];
  let protected_ = '';
  let pos = 0;
  while (pos < formula.length) {
    if (formula.startsWith('\\text{', pos)) {
      let depth = 1;
      let end = pos + 6;
      while (end < formula.length && depth > 0) {
        if (formula[end] === '{') depth++;
        else if (formula[end] === '}') depth--;
        end++;
      }
      texts.push(formula.substring(pos, end));
      protected_ += `\x00T${texts.length - 1}\x00`;
      pos = end;
    } else {
      protected_ += formula[pos];
      pos++;
    }
  }
  // 2. 将剩余的中文序列包裹在 \text{} 中
  protected_ = protected_.replace(CJK_RE, (cjk) => `\\text{${cjk}}`);
  // 3. 还原受保护的 \text{} 块
  protected_ = protected_.replace(/\x00T(\d+)\x00/g, (_, i) => texts[parseInt(i, 10)]);
  return protected_;
};

const wrapCjkInMathContent = (content: string): string =>
  content.replace(MATH_BLOCK, (match) => {
    if (match.startsWith('$$')) {
      return `$$${wrapCjkInFormula(match.slice(2, -2))}$$`;
    }
    return `$${wrapCjkInFormula(match.slice(1, -1))}$`;
  });

const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({
  content, enableMermaid = true, enableMath = true,
  className = '', mermaidDefaultView = 'code',
}) => {
  const remarkPlugins = useMemo(() => {
    const plugins: any[] = [remarkGfm];
    if (enableMath) plugins.push(remarkMath);
    return plugins;
  }, [enableMath]);

  const rehypePlugins = useMemo(() => {
    const plugins: any[] = [];
    if (enableMath) plugins.push([rehypeKatex, { strict: false, throwOnError: false }]);
    return plugins;
  }, [enableMath]);

  const processedContent = useMemo(() => {
    if (!enableMath) return content;
    return wrapCjkInMathContent(
      content
        .replace(/\\\[([\s\S]*?)\\\]/g, (_: string, formula: string) => `\n\n$$\n${formula.trim()}\n$$\n\n`)
        .replace(/\\\(([\s\S]*?)\\\)/g, (_: string, formula: string) => `$${formula.trim()}$`)
    );
  }, [content, enableMath]);

  const MERMAID_FENCE_PATTERN = '```mermaid[\\t ]*\\r?\\n([\\s\\S]*?)```';

  const mermaidBlocks = useMemo(() => {
    if (!enableMermaid) return { charts: [] as string[] };
    const charts: string[] = [];
    const regex = new RegExp(MERMAID_FENCE_PATTERN, 'g');
    let match;
    while ((match = regex.exec(processedContent)) !== null) charts.push(match[1].trim());
    return { charts };
  }, [processedContent, enableMermaid]);

  const contentWithoutMermaid = useMemo(() => {
    if (!enableMermaid || mermaidBlocks.charts.length === 0) return processedContent;
    let idx = 0;
    const regex = new RegExp(MERMAID_FENCE_PATTERN, 'g');
    return processedContent.replace(regex, () => `\n\n<div class="mermaid-placeholder" data-index="${idx++}"></div>\n\n`);
  }, [processedContent, enableMermaid, mermaidBlocks.charts.length]);

  const nonMermaidComponents = useMemo(() => ({
    pre: ({ children }: any) => {
      const codeProps = children?.props;
      const codeClassName: string = codeProps?.className || '';
      const rawCode = extractTextFromChildren(codeProps?.children);
      const language = codeClassName.replace('language-', '');
      return (
        <div className="relative my-3">
          {language && <div className="absolute top-0 right-0 bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-bl font-mono">{language}</div>}
          <pre className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-x-auto text-sm text-gray-900">
            <code className={codeClassName}>{rawCode}</code>
          </pre>
        </div>
      );
    },
    code: ({ className: codeCls, children }: any) => {
      if (codeCls) return <code className={codeCls}>{children}</code>;
      return <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-pink-600">{children}</code>;
    },
    p: ({ children }: any) => <p className="mb-3 leading-relaxed text-sm">{children}</p>,
    ul: ({ children }: any) => <ul className="mb-4 pl-5 space-y-1.5 list-disc">{children}</ul>,
    ol: ({ children }: any) => <ol className="mb-4 pl-5 space-y-1.5 list-decimal">{children}</ol>,
    li: ({ children }: any) => <li className="text-sm leading-relaxed">{children}</li>,
    h1: ({ children }: any) => <h1 className="text-lg font-semibold mb-3 text-gray-900 border-b border-gray-200 pb-2">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-base font-semibold mb-2 text-gray-900">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-sm font-semibold mb-2 text-gray-800">{children}</h3>,
    strong: ({ children }: any) => <strong className="font-semibold text-gray-900">{children}</strong>,
    em: ({ children }: any) => <em className="italic text-gray-700">{children}</em>,
    blockquote: ({ children }: any) => <blockquote className="border-l-4 border-green-200 pl-4 my-3 italic text-gray-600">{children}</blockquote>,
    table: ({ children }: any) => <table className="w-full border-collapse border border-gray-300 my-3">{children}</table>,
    thead: ({ children }: any) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }: any) => {
      const processed = React.Children.map(children, (child) => {
        if (typeof child === 'string') return child.split('<br>').map((part, i) => i === 0 ? part : <React.Fragment key={i}><br />{part}</React.Fragment>);
        return child;
      });
      return <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-xs">{processed}</th>;
    },
    td: ({ children }: any) => {
      const processed = React.Children.map(children, (child) => {
        if (typeof child === 'string') return child.split('<br>').map((part, i) => i === 0 ? part : <React.Fragment key={i}><br />{part}</React.Fragment>);
        return child;
      });
      return <td className="border border-gray-300 px-3 py-2 text-xs">{processed}</td>;
    },
    a: ({ href, children }: any) => <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
    br: () => <br className="my-1" />,
  }), []);

  return (
    <div className={`prose max-w-none ${className} katex-protected`}>
      <style>{`
        /* 仅容器级设置字体回退，不覆盖 KaTeX 内部 font class（如 KaTeX_Math / KaTeX_AMS） */
        .katex-protected .katex {
          font-family: KaTeX_Main, 'Times New Roman', 'PingFang SC', 'Microsoft YaHei', serif !important;
          line-height: 1.2;
          white-space: nowrap;
        }
        .katex-protected .katex-display {
          font-family: KaTeX_Main, 'Times New Roman', 'PingFang SC', 'Microsoft YaHei', serif !important;
          white-space: normal;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 0.5em 0;
        }
        .katex-protected .katex { font-size: 1.1em !important; }
        .katex-protected .katex-display > .katex { font-size: 1.21em !important; }
        .katex-protected .katex-display > .katex > .katex-html { text-align: center; }
      `}</style>
      {mermaidBlocks.charts.length > 0 ? (
        <SplitMermaidContent content={contentWithoutMermaid} charts={mermaidBlocks.charts}
          remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins}
          components={nonMermaidComponents} mermaidDefaultView={mermaidDefaultView} />
      ) : (
        <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={nonMermaidComponents}>
          {processedContent}
        </ReactMarkdown>
      )}
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';
export default MarkdownRenderer;
